/**
 * BRSR Framework Configuration
 *
 * graphConfigs — flat map of chartKey → chart config.
 *   Each entry has either:
 *     dataConfigs: [{ label, widgetConfig: { module: ["X"], ... } }, ...]
 *   or:
 *     widgetConfig: { module: ["X"], ... }
 *
 *   Module is always a single-element array; all dataConfigs within one
 *   chart config share the same module (guaranteed by API contract).
 *
 *   When fetching, call the preview API ONCE PER dataConfig, then combine
 *   results before passing to ChartFactory.
 */

const blank = (overrides = {}) => ({
  financial_year:  [],
  location_id:     [],
  sublocation_id:  [],
  category:        [],
  sub_category:    [],
  kpi:             [],
  displayPeriods:  [],
  ...overrides,
});

// ─── Summary Configs (per-module) ─────────────────────────────────────────────

const summaryConfigs = {
  Energy: {
    module: "Energy",
    cards: [
      {
        id: "energy_renewable_percentage",
        title: "Renewable Energy Percentage",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "neutral",
        skipComparisonView: true,
        comparison: {
          type: "dimension", 
          field: "sub_category",
          values: ["Renewable", "Non-renewable"],
          comparisonType: "percentage_share"
        },
        group_by: ["sub_category"],
        flatten_from: ["DC1", "DC2"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Energy"],
              filters: blank({ category: ["Energy Consumption"], sub_category: ["Renewable"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          },
          {
            widgetConfig: {
              module: ["Energy"],
              filters: blank({ category: ["Energy Consumption"], sub_category: ["Non-renewable"], kpi: ["GRID Electricity"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "energy_from_renewable",
        title: "Energy from Renewable",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "up",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["sub_category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Energy"],
              filters: blank({ category: ["Energy Consumption"], sub_category: ["Renewable"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "energy_from_non_renewable",
        title: "Energy from Non-Renewable",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Energy"],
              filters: blank({ category: ["Energy Consumption"], sub_category: ["Non-Renewable"], kpi: ["GRID Electricity"] }),
              group_by: ["category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "energy_from_fuels",
        title: "Energy from Fuels",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Energy"],
              filters: blank({ category: ["Energy Consumption"], sub_category: ["Non-Renewable"], kpi: ["Petrol", "LPG", "PNG", "Diesel"] }),
              group_by: ["category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
    ],
  },

  Water: {
    module: "Water",
    cards: [
      {
        id: "total_water_consumption",
        title: "Total Water Consumption",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Water"],
              filters: blank({ category: ["Withdrawal"] }),
              group_by: ["category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "total_water_discharged",
        title: "Total Water Discharged",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Water"],
              filters: blank({ category: ["Discharge"] }),
              group_by: ["category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
    ],
  },

  Emission: {
    module: "Emission",
    cards: [
      {
        id: "scope1_emissions",
        title: "Scope 1 Emissions",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["sub_category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Emission"],
              filters: blank({ category: ["Emission"], sub_category: ["Scope 1"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "scope2_emissions",
        title: "Scope 2 Emissions",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["sub_category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Emission"],
              filters: blank({ category: ["Emission"], sub_category: ["Scope 2"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
    ],
  },

  Waste: {
    module: "Waste",
    cards: [

      {
        id: "total_waste_generated",
        title: "Total Waste Generated",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["sub_category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Waste"],
              filters: blank({ category: ["Waste"], sub_category: ["Generated"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      }
    ],
  },

  "Governance": {
    module: "Governance",
    cards: [
      {
        id: "customer_complaints",
        title: "Customer Complaints",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["kpi"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Governance"],
              filters: blank({ category: ["Complaints"], sub_category: ["Customer"], kpi: ["Received"] }),
              group_by: ["kpi"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            },
          }
        ]
      },
    ],
  },

};

// ─── Graph Configs — flat map, module embedded in each widgetConfig ───────────
// Exactly matches the provided config JSON format.
// dataConfigs path: each entry is fetched separately, then results are combined.
// widgetConfig path: fetched once, passed to ChartFactory with transform: true.
const graphConfigs = {

  Energy: {
    title: "Energy",
    description: "",
    chartType: "donut",
    groupChartType: "column",
    enableStacking: true,
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["sub_category"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Energy"],
          filters: blank({ category: ["Energy Consumption"], sub_category: ["Renewable"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
      {
        widgetConfig: {
          module: ["Energy"],
          filters: blank({ category: ["Energy Consumption"], sub_category: ["Non-renewable"], kpi: ["GRID Electricity"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  "Fuel Energy": {
    title: "Fuel Energy",
    description: "",
    chartType: "donut",
    groupChartType: "column",
    enableStacking: true,
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["kpi"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Energy"],
          filters: blank({ category: ["Energy Consumption"], sub_category: ["Non-renewable"], kpi: ["Petrol", "LPG", "PNG", "Diesel"] }),
          group_by: ["kpi"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  Fuel: {
    title: "Fuel",
    chartType: "bar",
    groupChartType: "bar",
    enableStacking: false,
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["kpi"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Energy"],
          filters: blank({ category: ["Fuel Consumption"] }),
          group_by: ["kpi"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  Emission: {
    title: "Emissions",
    chartType: "donut",
    groupChartType: "column",
    enableStacking: false,
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["sub_category"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Emission"],
          filters: blank({ category: ["Emission"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  "Water Consumption": {
    title: "Water Consumption",
    chartType: "donut",
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["kpi"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Water"],
          filters: blank({ category: ["Withdrawal"] }),
          group_by: ["kpi"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  Waste: {
    title: "Waste",
    chartType: "bar",
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["sub_category"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Waste"],
          filters: blank({ category: ["Waste"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

};

const brsrConfig = {
  label: "BRSR",
  description: "Business Responsibility & Sustainability Report (SEBI, India)",
  apiKeys: ["brsr", "business responsibility"],
  modules: [
    "Energy", "Water", "Emission", "Waste", "Governance"
  ],
  summaryConfigs,
  graphConfigs,
};

export default brsrConfig;